import { type StateStore, type UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';

/**
 * The local dispatcher that routes one registered redirect URI to several dev
 * instances, or undefined when there is none.
 *
 * Running more than one instance puts each on its own port, which the identity
 * provider does not know. A dispatcher on the registered port hands each code
 * back to the instance that asked for it. `rspack.config.ts` substitutes this as
 * undefined outside development, so a deployed build never reaches one.
 */
const configured = process.env.REACT_APP_AUTH_DISPATCHER;
const isDev = process.env.NODE_ENV === 'development';

// An origin, not the raw value: the renew frame's origin and OneLogin both match it exactly.
export const authDispatcher = configured && isDev ? new URL(configured).origin : undefined;

/**
 * Reserves each request's state with the dispatcher, which is what lets it hand
 * the code back to this instance. The redirect and the silent-renew flows both
 * persist their state here, so one wrapper covers them.
 */
function claimingStateStore(dispatcher: string): StateStore {
  const inner = new WebStorageStateStore({ store: window.localStorage });
  return {
    set: async (state, value) => {
      const claimed = await fetch(`${dispatcher}/claim`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ state, origin: window.location.origin }),
      });
      if (!claimed.ok) {
        throw new Error(`auth dispatcher refused the claim (${claimed.status})`);
      }
      return inner.set(state, value);
    },
    get: (state) => inner.get(state),
    remove: (state) => inner.remove(state),
    getAllKeys: () => inner.getAllKeys(),
  };
}

/**
 * The endpoints the provider grants CORS to a registered origin only. They are
 * fetched rather than navigated to, so an instance on its own port reaches them
 * through the dev server, which mirrors the authority's path.
 */
function relayedEndpoints(authority: string): { token_endpoint: string; revocation_endpoint: string } {
  const path = new URL(authority).pathname;
  return {
    token_endpoint: `${window.location.origin}${path}/token`,
    revocation_endpoint: `${window.location.origin}${path}/token/revocation`,
  };
}

/**
 * The settings that running behind the dispatcher overrides, or nothing when
 * there is none. Spread last over the provider's own, so the normal
 * configuration is what a reader sees and this is visibly the exception.
 */
export function dispatcherSettings(authority: string): Partial<UserManagerSettings> {
  if (!isDev || !authDispatcher) return {};

  return {
    redirect_uri: authDispatcher,
    silent_redirect_uri: `${authDispatcher}/silent-renew.html`,
    // UserManager and its OidcClient each build a settings store from what they are
    // given, so the store has to be passed in to be the one actually used.
    stateStore: claimingStateStore(authDispatcher),
    metadataSeed: relayedEndpoints(authority),
  };
}
